import React, { useState, useMemo } from 'react'
import { Table, Space, Tag, Modal, Button } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDate } from '../../utils/dateTime'
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags'

type FulfillmentItemStatus =
  | 'pending'
  | 'allocated'
  | 'partial_allocated'
  | 'out_of_stock'
  | 'non_inventoried'
  | 'canceled'

interface InventoryAllocation {
  id?: string
  batch?: string | null
  expiration_date?: string | null
  manufacturing_date?: string | null
  custom_attributes?: Record<string, unknown> | null
  inventory_type?: {
    id?: string
    name?: string | null
    code?: string | null
  } | null
  quantity?: number
  [key: string]: unknown
}

interface InventoryAllocationWithProduct extends InventoryAllocation {
  product_sku?: string | null
  product_name?: string | null
}

interface FulfillmentItemRecord {
  product?: {
    id?: string
    sku?: string | null
    name?: string | null
  } | null
  unknown_product?: {
    sku?: string | null
    name?: string | null
  } | null
  inventory_type?: {
    name?: string | null
  } | null
  batch?: string | null
  manufacturing_date?: string | null
  expiration_date?: string | null
  custom_attributes?: Record<string, unknown> | null
  quantity?: number
  shortage_quantity?: number
  status?: FulfillmentItemStatus | string
  inventories?: InventoryAllocation[]
  [key: string]: unknown
}

interface UseInventoryModalOptions {
  // name → label 對照表，避免 <CustomAttributesTags> 顯示英文 raw key（例：manufacturing_plant）。
  // 呼叫端用 useInventoryAttributeDefinitions(role, merchantId) 拉定義後 map 成這形狀傳進來。
  attributeLabelByName?: Record<string, string>
}

/**
 * 庫存分配 Modal hook
 *
 * 提供：
 * - 單品項庫存分配 Modal（點數量或狀態觸發）
 * - 全品項庫存彙總 Modal（點 summary 總數觸發）
 * - 共用的 fulfillment item columns
 * - 共用的 inventory modal columns
 */
export default function useInventoryModal({ attributeLabelByName }: UseInventoryModalOptions = {}) {
  const { t } = useTranslation()
  const [inventoryModalItem, setInventoryModalItem] = useState<FulfillmentItemRecord | null>(null)
  const [allInventoriesData, setAllInventoriesData] = useState<InventoryAllocationWithProduct[] | null>(null)

  // 庫存分配 Modal 欄位
  const inventoryModalColumns = useMemo<ColumnsType<InventoryAllocation>>(() => [
    { title: t('models.order_item.batch'), dataIndex: 'batch', key: 'batch', render: (text) => text || '-' },
    { title: t('models.order_item.expiration_date'), dataIndex: 'expiration_date', key: 'expiration_date', render: (text) => text ? formatDate(text) : '-' },
    { title: t('models.inventory.inventory_type'), key: 'inventory_type', render: (_, record) => record.inventory_type?.name || '-' },
    { title: t('models.inventory.manufacturing_date'), dataIndex: 'manufacturing_date', key: 'manufacturing_date', render: (text) => text ? formatDate(text) : '-' },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (attrs) => <CustomAttributesTags attributes={attrs} attributeLabelByName={attributeLabelByName} />,
    },
    { title: t('models.order_item_inventories.allocated_quantity'), dataIndex: 'quantity', key: 'quantity', align: 'right' },
  ], [t, attributeLabelByName])

  // 全品項庫存彙總 Modal 欄位（多加商品 SKU/名稱）
  const allInventoriesModalColumns = useMemo<ColumnsType<InventoryAllocationWithProduct>>(() => [
    { title: t('models.product.sku'), dataIndex: 'product_sku', key: 'product_sku' },
    { title: t('models.product.name'), dataIndex: 'product_name', key: 'product_name' },
    ...(inventoryModalColumns as ColumnsType<InventoryAllocationWithProduct>),
  ], [t, inventoryModalColumns])

  // 出貨品項狀態 Tag
  const getItemStatusTag = (status: FulfillmentItemStatus | string | undefined) => {
    const statusConfig: Record<string, { color: string }> = {
      pending: { color: 'default' },
      allocated: { color: 'success' },
      partial_allocated: { color: 'warning' },
      out_of_stock: { color: 'error' },
      non_inventoried: { color: 'blue' },
      canceled: { color: 'default' },
    }
    const config = (status && statusConfig[status]) || { color: 'default' }
    return (
      <Tag color={config.color}>
        {t(`enums.fulfillment_item.status.${status}`, { defaultValue: status })}
      </Tag>
    )
  }

  // 共用的 fulfillment item 欄位
  const fulfillmentItemColumns = useMemo<ColumnsType<FulfillmentItemRecord>>(() => [
    {
      title: t('models.product.name'),
      key: 'product_name',
      render: (_, record) => {
        const sku = record.product?.sku || record.unknown_product?.sku
        if (record.unknown_product) {
          const name = record.unknown_product?.name || '-'
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Space>
                <Tag color="warning">{t('models.unknown_product._')}</Tag>
                <span style={{ color: '#faad14', fontWeight: 500 }}>{name}</span>
              </Space>
              {sku && <span style={{ fontSize: 12, color: '#faad14' }}>{sku}</span>}
            </div>
          )
        }
        const name = record.product?.name || '-'
        const nameEl = record.product?.id
          ? <Link to={`/products/${record.product.id}`}>{name}</Link>
          : <span>{name}</span>
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {nameEl}
            {sku && <span style={{ fontSize: 12, color: '#86909c' }}>{sku}</span>}
          </div>
        )
      },
    },
    {
      title: t('models.inventory.inventory_type'),
      key: 'inventory_type',
      render: (_, record) => record.inventory_type?.name || '-',
    },
    {
      title: t('models.order_item.batch'),
      dataIndex: 'batch',
      key: 'batch',
      render: (batch) => batch || '-',
    },
    {
      title: t('models.order_item.expiration_info'),
      key: 'expiration_info',
      render: (_, record) => {
        const mfg = record.manufacturing_date
        const exp = record.expiration_date
        if (!mfg && !exp) return '-'
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12, lineHeight: 1.4 }}>
            {mfg && <span><span style={{ color: '#86909c' }}>{t('models.order_item.manufacturing_date')}</span> {formatDate(mfg)}</span>}
            {exp && <span><span style={{ color: '#86909c' }}>{t('models.order_item.expiration_date')}</span> {formatDate(exp)}</span>}
          </div>
        )
      },
    },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      render: (custom_attributes) => <CustomAttributesTags attributes={custom_attributes} attributeLabelByName={attributeLabelByName} />,
    },
    {
      title: t('models.order_item.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (quantity, record) => {
        if (record.inventories && record.inventories.length > 0) {
          return (
            <Button type="link" size="small" style={{ padding: 0, fontSize: 16, fontWeight: 'bold' }} onClick={() => setInventoryModalItem(record)}>
              {quantity}
            </Button>
          )
        }
        return quantity
      },
    },
    {
      title: t('shared.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const allocated = record.inventories?.reduce((sum, i) => sum + (i.quantity || 0), 0) || 0
        const hasInventories = record.inventories && record.inventories.length > 0
        const content = (
          <Space direction="vertical" size={2}>
            {getItemStatusTag(status)}
            {(record.shortage_quantity ?? 0) > 0 && record.status !== 'canceled' && (
              <span style={{ fontSize: '12px', color: '#ff4d4f' }}>
                {t('models.fulfillment_item.shortage')}: {record.shortage_quantity}
              </span>
            )}
            {allocated > 0 && record.quantity !== undefined && allocated < record.quantity && (
              <span style={{ fontSize: '12px', color: '#888' }}>
                {allocated} / {record.quantity}
              </span>
            )}
          </Space>
        )
        if (hasInventories) {
          return <a onClick={() => setInventoryModalItem(record)}>{content}</a>
        }
        return content
      },
    },
  ], [t, attributeLabelByName])

  /**
   * 開啟全品項庫存彙總 Modal
   * @param inventories - 帶 product_sku, product_name 的庫存分配陣列
   */
  const showAllInventories = (inventories: InventoryAllocationWithProduct[]) => setAllInventoriesData(inventories)

  /**
   * 渲染 Modals（放在 JSX 最後）
   */
  const renderInventoryModals = () => (
    <>
      {/* 單品項庫存分配 Modal */}
      <Modal
        title={
          <Space>
            <span>{t('models.order_item.inventory_allocations')}</span>
            {inventoryModalItem?.product && (
              <span style={{ color: '#888' }}>— {inventoryModalItem.product.sku} {inventoryModalItem.product.name}</span>
            )}
          </Space>
        }
        open={!!inventoryModalItem}
        onCancel={() => setInventoryModalItem(null)}
        footer={null}
        width={800}
      >
        {inventoryModalItem && (
          <Table
            columns={inventoryModalColumns}
            dataSource={inventoryModalItem.inventories}
            pagination={false}
            rowKey="id"
            size="small"
            bordered
          />
        )}
      </Modal>

      {/* 全品項庫存彙總 Modal */}
      <Modal
        title={t('models.order_item.inventory_allocations')}
        open={!!allInventoriesData}
        onCancel={() => setAllInventoriesData(null)}
        footer={null}
        width={900}
      >
        {allInventoriesData && (
          <Table
            columns={allInventoriesModalColumns}
            dataSource={allInventoriesData}
            pagination={false}
            rowKey="id"
            size="small"
            bordered
          />
        )}
      </Modal>
    </>
  )

  return {
    fulfillmentItemColumns,
    inventoryModalColumns,
    showAllInventories,
    renderInventoryModals,
  }
}
